import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { ReactNode } from "react";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";

export interface FeedbackBannerProps {
  variant: "error" | "warning" | "info";
  children: ReactNode;
  className?: string;
  iconSize?: "sm" | "md" | "lg";
  contentAlignment?: "start" | "center";
}

const iconSizeConfig = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

const variantConfig = {
  error: {
    bgColor: "bg-red-5",
    borderColor: "border-red-50",
    iconColor: "text-red-50",
    icon: XCircleIcon,
  },
  warning: {
    bgColor: "bg-gold-5",
    borderColor: "border-gold-50",
    iconColor: "text-gold-50",
    icon: ExclamationTriangleIcon,
  },
  info: {
    bgColor: "bg-blue-5",
    borderColor: "border-blue-50",
    iconColor: "text-blue-50",
    icon: InformationCircleIcon,
  },
} as const;

export const FeedbackBanner = ({
  variant,
  children,
  className,
  iconSize = "md",
  contentAlignment = "start",
}: FeedbackBannerProps) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Stack
      direction="row"
      align={contentAlignment}
      gap="sm"
      className={cn(
        "rounded-lg border p-4",
        config.bgColor,
        config.borderColor,
        className,
      )}
    >
      <Icon
        className={cn(
          "flex-shrink-0",
          iconSizeConfig[iconSize],
          config.iconColor,
        )}
      />
      <div className="flex-1">{children}</div>
    </Stack>
  );
};
